// 261010 P8b-2 — timeline Ghép (thuần: không antd/router). Nhận `status.events` nguyên. Không poster, không "đã duyệt".
import type { WorkflowEventStatus } from '../../types/filmWorkflow';
import { formatClock } from './workflowBoardUtils';

export const TIMELINE_MIN_PX = 78;
export const TIMELINE_PX_PER_S = 8;

export type TimelineCellKind = 'selected' | 'unselected' | 'missing';

export interface TimelineCell {
  eventId: string;
  eventOrder: number;
  sceneId: string;
  sceneOrder: number;
  kind: TimelineCellKind;
  durationS: number | null;
  widthPx: number;
  clock: string;
  posClock: string;
  label: string;
}

export interface TimelineSceneGroup {
  sceneId: string;
  sceneOrder: number;
  heading: string;
  cells: TimelineCell[];
}

export interface TimelineView {
  groups: TimelineSceneGroup[];
  selectedDurationS: number;
  expectedDurationS: number;
  selectedCount: number;
  clipCount: number;
  total: number;
  missingOrders: number[];
  endClock: string;
  summary: string;
}

const KIND_LABEL: Record<TimelineCellKind, string> = {
  selected: 'đã chọn clip',
  unselected: 'chưa chọn',
  missing: 'thiếu clip',
};

function cellKind(e: WorkflowEventStatus): TimelineCellKind {
  if (e.card?.selectedClip) return 'selected';
  const vs = e.steps?.VIDEO;
  if (vs === 'READY' || vs === 'APPROVED') return 'unselected';
  return 'missing';
}

function durationOf(e: WorkflowEventStatus): number | null {
  const d = e.card?.selectedClip?.durationS;
  if (typeof d === 'number' && Number.isFinite(d) && d >= 0) return d;
  if (typeof e.genDurationS === 'number' && Number.isFinite(e.genDurationS) && e.genDurationS >= 0) return e.genDurationS;
  return null;
}

function expectedOf(e: WorkflowEventStatus): number {
  return typeof e.genDurationS === 'number' && Number.isFinite(e.genDurationS) && e.genDurationS > 0 ? e.genDurationS : 0;
}

function selectedDur(e: WorkflowEventStatus): number {
  if (!e.card?.selectedClip) return 0;
  const d = e.card.selectedClip.durationS;
  if (typeof d === 'number' && Number.isFinite(d) && d >= 0) return d;
  return expectedOf(e);
}

export function timelineOf(events: WorkflowEventStatus[]): TimelineView {
  const sorted = [...events].sort((a, b) => a.sceneOrder - b.sceneOrder || a.eventOrder - b.eventOrder);
  const groups: TimelineSceneGroup[] = [];
  let pos = 0;
  let selectedDurationS = 0;
  let expectedDurationS = 0;
  let selectedCount = 0;
  let clipCount = 0;
  const missingOrders: number[] = [];
  for (const e of sorted) {
    const kind = cellKind(e);
    const durationS = durationOf(e);
    const widthPx = durationS == null ? TIMELINE_MIN_PX : Math.max(TIMELINE_MIN_PX, Math.round(durationS * TIMELINE_PX_PER_S));
    const cell: TimelineCell = {
      eventId: e.eventId, eventOrder: e.eventOrder, sceneId: e.sceneId, sceneOrder: e.sceneOrder,
      kind, durationS, widthPx, clock: formatClock(durationS), posClock: formatClock(pos), label: KIND_LABEL[kind],
    };
    let g = groups.find((x) => x.sceneId === e.sceneId);
    if (!g) { g = { sceneId: e.sceneId, sceneOrder: e.sceneOrder, heading: e.sceneHeading || `Cảnh ${e.sceneOrder}`, cells: [] }; groups.push(g); }
    g.cells.push(cell);
    pos += durationS ?? 0;
    expectedDurationS += expectedOf(e);
    if (kind === 'selected') { selectedCount += 1; clipCount += 1; selectedDurationS += selectedDur(e); }
    else if (kind === 'unselected') clipCount += 1;
    if (kind === 'missing') missingOrders.push(e.eventOrder);
  }
  const missTxt = missingOrders.length ? ` · thiếu Video ${missingOrders.join(', ')}` : '';
  const summary = `Tổng ${formatClock(selectedDurationS)} (đã chọn) · dự kiến ${formatClock(expectedDurationS)} khi đủ ${sorted.length} video · ${clipCount}/${sorted.length} video có clip${missTxt}`;
  return { groups, selectedDurationS, expectedDurationS, selectedCount, clipCount, total: sorted.length, missingOrders, endClock: formatClock(pos), summary };
}
